import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useStatusSichtbarkeit } from "@/hooks/useStatusSichtbarkeit";
import { useCurrentUser, userIsAbrechnung, userIsBuero } from "@/lib/auth";
import {
  getLieferscheinColumnSettings,
  lieferscheinAtContactFields,
  listLieferscheineSummary,
  saveLieferscheinColumnSettings,
  type LieferscheinSummary,
} from "@/lib/lieferscheine";
import { SearchInput } from "@/pages/projekte/SearchInput";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { LieferscheinSummaryPanels } from "@/pages/lieferscheine/LieferscheinSummaryPanels";
import { LieferscheinTable } from "@/pages/lieferscheine/LieferscheinTable";
import {
  getLieferscheinContactAgeInfo,
  getLieferscheinContactName,
  getLieferscheinContactSearchTerm,
  lieferscheinIsS,
  lieferscheinYear,
} from "@/pages/lieferscheine/lieferscheinListLogic";
import { clampTimesheetColumnWidth } from "@/pages/stundennachweise/columns";

type StatusFilter =
  | "all"
  | "erstellt"
  | "versendet"
  | "signed"
  | "assigned"
  | "invoice_draft"
  | "invoice_created"
  | "archived";

type YearFilter = number | "S" | "all";

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "erstellt", label: "Erstellt" },
  { value: "versendet", label: "Versendet" },
  { value: "signed", label: "Geliefert" },
  { value: "assigned", label: "Zugewiesen" },
  { value: "invoice_draft", label: "Rechnungsentwurf" },
  { value: "invoice_created", label: "Rechnung" },
  { value: "archived", label: "Archiviert" },
];

const CURRENT_YEAR = new Date().getFullYear();

export function LieferscheineIndexPage() {
  const { user } = useCurrentUser();
  const { openLieferscheinDetail, refreshKey } = useTimesheetFlow();
  const isAbrechnung = userIsAbrechnung(user);
  const isBuero = userIsBuero(user);
  const { visibleStatuses } = useStatusSichtbarkeit();
  const statusFilterOptions = useMemo(() => {
    const visible = new Set(visibleStatuses("lieferscheine"));
    return STATUS_FILTER_OPTIONS.filter(
      (opt) =>
        opt.value === "all" ||
        opt.value === "erstellt" ||
        opt.value === "versendet" ||
        visible.has(opt.value),
    );
  }, [visibleStatuses]);

  const [rows, setRows] = useState<LieferscheinSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [contactAgingSearch, setContactAgingSearch] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [yearFilter, setYearFilter] = useState<YearFilter>("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const widthSaveTimerRef = useRef<number | null>(null);
  const pendingWidthsRef = useRef<Record<string, number> | null>(null);
  const hasRowsRef = useRef(false);

  const loadArchived = includeArchived || statusFilter === "archived";

  const reload = useCallback(async () => {
    if (!hasRowsRef.current) setLoading(true);
    setError(null);
    try {
      const list = await listLieferscheineSummary({ includeArchived: loadArchived });
      hasRowsRef.current = list.length > 0;
      setRows(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lieferscheine konnten nicht geladen werden.");
      hasRowsRef.current = false;
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [loadArchived]);

  useEffect(() => {
    void reload();
  }, [reload, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    getLieferscheinColumnSettings()
      .then((settings) => {
        if (cancelled) return;
        setWidths(settings.desktop?.widths ?? {});
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (widthSaveTimerRef.current) window.clearTimeout(widthSaveTimerRef.current);
    };
  }, []);

  const patchAtContact = useCallback((dokumentId: number, atContact: boolean) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === dokumentId ? { ...row, ...lieferscheinAtContactFields(atContact) } : row,
      ),
    );
  }, []);

  const handleColumnWidthChange = useCallback((key: string, width: number) => {
    const clamped = clampTimesheetColumnWidth(width);
    setWidths((prev) => {
      const next = { ...prev, [key]: clamped };
      pendingWidthsRef.current = next;
      return next;
    });
    if (widthSaveTimerRef.current) window.clearTimeout(widthSaveTimerRef.current);
    widthSaveTimerRef.current = window.setTimeout(() => {
      const payload = pendingWidthsRef.current;
      pendingWidthsRef.current = null;
      widthSaveTimerRef.current = null;
      if (!payload) return;
      void saveLieferscheinColumnSettings({
        desktop: { columns: [], widths: payload },
      }).catch(() => undefined);
    }, 500);
  }, []);

  const yearChips = useMemo(() => {
    const set = new Set<number>();
    let hasS = false;
    for (const row of rows) {
      if (lieferscheinIsS(row)) hasS = true;
      const year = lieferscheinYear(row);
      if (year) set.add(year);
    }
    set.add(CURRENT_YEAR);
    if (typeof yearFilter === "number") set.add(yearFilter);
    return { years: [...set].sort((a, b) => b - a), hasS };
  }, [rows, yearFilter]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((row) => {
      if (yearFilter === "S") {
        if (!lieferscheinIsS(row)) return false;
      } else if (typeof yearFilter === "number") {
        if (lieferscheinIsS(row) || lieferscheinYear(row) !== yearFilter) return false;
      }
      if (statusFilter !== "all" && row.billing_status !== statusFilter) {
        if (!(statusFilter === "signed" && row.billing_status === "geliefert")) {
          if (!(statusFilter === "erstellt" && row.status === "erstellt")) {
            if (!(statusFilter === "versendet" && row.status === "versendet")) {
              return false;
            }
          }
        }
      }
      if (needle) {
        const hay = [
          row.display_number,
          row.dokument_nummer,
          row.kunde_name,
          row.kunde,
          row.auftrag_nummer,
          row.auftragsnummer,
          row.auftrag_display,
          row.ordnernummer,
          row.auftrag_beschreibung,
          row.betreff,
          row.ansprechpartner,
          getLieferscheinContactName(row),
          getLieferscheinContactSearchTerm(row),
          row.billing_assignee_name,
          row.rechnung_nummer,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (needle && contactAgingSearch && !getLieferscheinContactAgeInfo(row)) return false;
      return true;
    });
  }, [rows, q, statusFilter, yearFilter, contactAgingSearch]);

  function handleSearchChange(value: string) {
    setQ(value);
    setContactAgingSearch(false);
  }

  function handleContactAgingFilter(term: string) {
    setQ(term);
    setContactAgingSearch(!!term.trim());
  }

  const countLabel =
    loading && filtered.length === 0
      ? "· Lade …"
      : filtered.length === 1
        ? "· 1 Lieferschein"
        : `· ${filtered.length} Lieferscheine`;

  const filtersActive =
    !!q.trim() ||
    statusFilter !== "all" ||
    yearFilter !== "all" ||
    includeArchived;

  if (!isBuero && !isAbrechnung) {
    return (
      <AppShell title="Lieferscheine">
        <Alert variant="error">Kein Zugriff auf die Lieferschein-Übersicht.</Alert>
      </AppShell>
    );
  }

  return (
    <AppShell title="Lieferscheine">
      <div className="space-y-4 md:space-y-5">
        <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5 md:py-4">
          <div className="flex flex-col gap-3 md:gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
            <div className="min-w-0 flex-1 space-y-1 lg:max-w-[min(100%,40rem)]">
              <h2 className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-lg font-semibold leading-snug tracking-tight text-label md:text-xl">
                <span className="min-w-0 break-words">Lieferscheine</span>
                <span
                  className="shrink-0 whitespace-nowrap text-sm font-medium tabular-nums text-label-secondary md:text-[15px]"
                  aria-live="polite"
                >
                  {countLabel}
                </span>
              </h2>
              <p className="text-xs text-label-secondary md:text-sm">
                Gelieferte und offene Lieferscheine inklusive Abrechnung.
              </p>
            </div>
            <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center lg:max-w-lg">
              <SearchInput
                value={q}
                onChange={handleSearchChange}
                placeholder="Lieferscheine durchsuchen…"
                className="min-w-0 flex-1"
              />
              <Button type="button" variant="secondary" onClick={() => void reload()} className="shrink-0">
                Aktualisieren
              </Button>
            </div>
          </div>
        </section>

        <section className="rounded-ios-lg bg-card px-4 py-4 ring-1 ring-separator/35 dark:ring-separator/50">
          <FilterLabel>Jahr</FilterLabel>
          <div className="mt-2 flex flex-wrap gap-2">
            {yearChips.years.map((year) => (
              <ChipButton
                key={year}
                active={yearFilter === year}
                onClick={() => setYearFilter(year)}
              >
                {year}
              </ChipButton>
            ))}
            {yearChips.hasS ? (
              <ChipButton active={yearFilter === "S"} onClick={() => setYearFilter("S")}>
                S‑Ordner
              </ChipButton>
            ) : null}
            <ChipButton active={yearFilter === "all"} onClick={() => setYearFilter("all")}>
              Alle
            </ChipButton>
          </div>

          <FilterLabel className="mt-3">Status</FilterLabel>
          <div className="mt-2 flex flex-wrap gap-2">
            {statusFilterOptions.map((opt) => (
              <ChipButton
                key={opt.value}
                active={statusFilter === opt.value}
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
              </ChipButton>
            ))}
          </div>

          <label className="mt-3 flex items-center gap-2 text-sm text-label-secondary">
            <input
              type="checkbox"
              checked={includeArchived || statusFilter === "archived"}
              onChange={(e) => {
                setIncludeArchived(e.target.checked);
                if (!e.target.checked && statusFilter === "archived") setStatusFilter("all");
              }}
            />
            Archiv anzeigen
          </label>
        </section>

        {error ? (
          <Alert variant="error" title="Liste konnte nicht geladen werden">
            {error}
          </Alert>
        ) : null}

        <section className="space-y-3">
          {!loading && filtered.length > 0 ? (
            <LieferscheinSummaryPanels
              rows={filtered}
              user={user}
              searchQuery={q}
              onContactAgingFilter={handleContactAgingFilter}
            />
          ) : null}

          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-label-secondary">
              <Spinner />
              <span className="ml-2 text-sm">Lieferscheine werden geladen …</span>
            </div>
          ) : (
            <>
              <LieferscheinTable
                rows={filtered}
                user={user}
                loading={loading}
                widths={widths}
                onRowOpen={(row) => openLieferscheinDetail(row.id, row.auftrag_id)}
                onContactToggled={patchAtContact}
                onColumnWidthChange={handleColumnWidthChange}
              />
              {!loading && filtered.length === 0 && !error && filtersActive ? (
                <div className="mt-4 flex flex-col items-center gap-3 rounded-ios bg-fill-quaternary/25 px-4 py-4 text-center shadow-ios ring-1 ring-separator/30 dark:ring-separator/45">
                  <p className="max-w-md text-sm text-label-secondary">
                    Für die aktuelle Auswahl wurden keine Lieferscheine gefunden. Filter
                    zurücksetzen, um die Chance auf Treffer zu erhöhen.
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setQ("");
                      setContactAgingSearch(false);
                      setStatusFilter("all");
                      setYearFilter("all");
                      setIncludeArchived(false);
                    }}
                  >
                    Filter zurücksetzen
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>

    </AppShell>
  );
}

function FilterLabel({ children, className }: { children: string; className?: string }) {
  return (
    <div className={clsx("text-[11px] font-medium uppercase tracking-wide text-label-tertiary", className)}>
      {children}
    </div>
  );
}

function ChipButton({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition",
        active
          ? "bg-accent text-label-on-accent shadow-sm"
          : "bg-fill-tertiary text-label hover:bg-fill",
      )}
    >
      {children}
    </button>
  );
}
